import { Col, DatePicker, Form, Input, InputNumber, message, Modal, Row, Select, Switch, Upload } from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { addInvoice, getallProject, getallProjectForReceipts, getAllReceipts, getCustomers, getInvoiceByID, getLastInvoiceNumber, getReceiptsByID, updateInvoice, updateReceipts } from "../../utils/API";
import { Currency } from "lucide-react";
import axios from "axios";

interface CopyInvoiceProps {
    visible: boolean;
    id: string;
    onClose?: () => void;
    onCancel?: () => void;
    onTaskCreated?: () => void;
    onSuccess?: () => void;
    // record?: ReceiptsFormValues | null;
}

interface InvoiceFormValues {
    _id: string;
    [key: string]: any;
    invoice_number: string;
    project: string;
    customer_name: string;
    amount: number;
    currency: string;
    invoice_date: string;
    due_date: string;
    description: string;
}

const CopyInvoiceModel: React.FC<CopyInvoiceProps> = (props) => {
    const [isLoading, setIsLoading] = useState(false);
    const [form] = Form.useForm();
    const [invoice, setInvoice] = useState<any>(null);
    const [customers, setCustomers] = useState<any>([]);
    const [invoiceParts, setInvoiceParts] = useState({
        prefix: "",
        year: "",
        seq: "",
    });
    const [projects, setProjects] = useState<any>([]);
    const [invoiceSeq, setInvoiceSeq] = useState(1);

    const onFinish = async (values: InvoiceFormValues) => {
        setIsLoading(true);
        try {
            const formData = new FormData();

            const invoiceNumber = generateInvoiceNumber(invoiceSeq);

            const finalValues = {
                ...values,
                invoice_number: invoiceNumber,
            };

            Object.entries(finalValues).forEach(([key, value]) => {
                if ((key === 'invoice_date' || key === 'due_date') && value && typeof value === 'object' && 'format' in value) {
                    formData.append(key, (value as any).format('DD-MM-YYYY'));
                } else if (value !== undefined && value !== null) {
                    formData.append(key, value.toString());
                }
            });

            // console.log("Form Data: ", formData);
            await addInvoice(formData);

            // getAllReceipts("");
            message.success("Invoice Added Successfully!");
            props.onSuccess?.();
        } catch (error) {
            console.error('Error submitting Invoice:', error);
            if (axios.isAxiosError(error)) {
                message.error(error.response?.data?.message || 'An error occurred.');
            } else {
                message.error('An unexpected error occurred.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const fetchInvoiceNumber = async () => {
        try {
            const response = await getLastInvoiceNumber();

            // 🔥 FIRST ENTRY CASE
            if (!response?.invoice_number) {
                setInvoiceSeq(1);
                return;
            }

            // Example: INV-2025-0042
            const parts = response.invoice_number.split("-");
            const lastSeq = parseInt(parts[2], 10);

            if (isNaN(lastSeq)) {
                setInvoiceSeq(1);
            } else {
                setInvoiceSeq(lastSeq + 1);
            }
        } catch (error) {
            console.error("Error fetching invoice number:", error);
            setInvoiceSeq(1); // fallback safe default
        }
    };

    useEffect(() => {
        if (invoice) {
            const parts = parseInvoiceNumber(invoice.invoice_number);
            setInvoiceParts(parts);

            form.setFieldsValue({
                customer_name: invoice.customer_name,
                project: invoice.project || undefined,
                amount: invoice.amount,
                currency: invoice.currency,
                invoice_date: dayjs(),
                due_date: invoice.due_date ? dayjs(invoice.due_date, "DD-MM-YYYY") : null,
                description: invoice.description,
            });
        }
    }, [invoice, form]);

    const fetchInvoice = async () => {
        setIsLoading(true);
        try {
            if (props.id) {
                const data = await getInvoiceByID(props.id);
                setInvoice(data);
            }
        } catch (error) {
            message.error("Failed to fetch invoice data.");
        } finally {
            setIsLoading(false);
        }
    }

    const fetchProjects = async () => {
        setIsLoading(true);
        try {
            const response: any = await getallProjectForReceipts("", "", true);

            if (response?.data) {
                const activeProjects = response.data.filter(
                    (client: any) => client.isActive === true
                );
                setProjects(activeProjects);
            }
        } catch (error) {
            console.error("Error fetching projects:", error);
        } finally {
            setIsLoading(false);
        }
    }

    const parseInvoiceNumber: any = (invoice: string) => {
        if (!invoice) return { prefix: "", year: "", seq: "" };

        const parts = invoice.split("-");
        return {
            prefix: parts[0] || "",
            year: parts[1] || "",
            seq: parts[2] || "",
        };
    };

    useEffect(() => {
        fetchInvoice();
        fetchProjects();
        fetchInvoiceNumber();
    }, [props.id]);

    useEffect(() => {
        const fetchCustomers = async () => {
            try {
                const response: any = await getCustomers("", "");

                if (response?.data) {
                    // Filter only active clients
                    const activeCustomers = response.data.filter(
                        (client: any) => client.isActive === true
                    );
                    setCustomers(activeCustomers);
                }
            } catch (error) {
                console.error("Error fetching customers:", error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchCustomers();
    }, []);

    useEffect(() => {
        if (props.visible) {


            // form.setFieldsValue({
            //     invoice_number: invoiceNumber,
            // });
        }
    }, [props.visible]);

    // Auto generate Receipts Number
    const prefix = "INV";
    const year = dayjs().format("YYYY");

    const formatSequence = (num: number) => num.toString().padStart(4, "0");

    const generateInvoiceNumber = (seq: number) => {
        return `${prefix}-${year}-${formatSequence(seq)}`;
    };

    // Watch the currently selected project
    const selectedProjectId = Form.useWatch("project", form);

    // Find the project object
    const selectedProject = projects.find((p: any) => p._id === selectedProjectId);

    // Filter customers: only show the customer linked to selected project
    const filteredCustomers = selectedProject
        ? customers.filter((c: any) => c._id === selectedProject.clientId?._id)
        : [];

    return (
        <Modal
            visible={props.visible}
            onCancel={props.onClose}
            footer={null}
            width={800}
            centered={true}
            destroyOnClose
            className="custom-modal"
            bodyStyle={{
                overflowY: "auto",
                fontFamily: "inter, sans-serif",
            }}
            style={{
                bottom: 20,
                top: 20,
                // maxHeight: 'calc(100vh - 30px)',
                margin: '0 auto',
                position: 'relative',
            }}
            styles={{
                header: {
                    padding: "12px",
                    position: "sticky",
                    top: 0,
                    backgroundColor: "#f1f5ffff",
                },
                content: {
                    padding: 0,
                    overflow: "hidden",
                },
            }}
            title={
                <div
                    style={{
                        color: "#4b5563",
                        fontFamily: "inter, sans-serif",
                        fontSize: "20px",
                        fontWeight: 500,
                    }}
                >
                    Copy Invoice
                </div>
            }
        >
            <Form onFinish={onFinish} form={form} layout="horizontal"
                labelCol={{ span: 5 }}
                wrapperCol={{ span: 19 }}
                colon={false} style={{ margin: 0 }}>
                <div className="elements">
                    <Form.Item
                        label={
                            <span style={{
                                color: '#4b5563',
                                fontFamily: 'inter, sans-serif',
                                fontSize: '14px',
                                paddingLeft: "15px",
                                fontWeight: 500,
                            }}>
                                Project
                            </span>
                        }
                        name="project"
                        required={false}
                    >
                        <Select
                            size="large"
                            allowClear
                            // disabled={roleId === 2}
                            showSearch
                            optionFilterProp="children"
                            loading={isLoading}
                            placeholder="Select Project"
                            className="CustomSelect"
                            onChange={(projectId) => {
                                const selectedProject = projects.find(
                                    (p: any) => p._id === projectId
                                );

                                if (selectedProject?.clientId?._id) {
                                    form.setFieldsValue({
                                        customer_name: selectedProject.clientId?._id
                                    });
                                }
                            }}
                        >
                            {projects.map((project: any) => (
                                <Select.Option key={project._id} value={project._id}>
                                    {project.projectName}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label={
                            <span style={{
                                color: '#4b5563',
                                fontFamily: 'inter, sans-serif',
                                fontSize: '14px',
                                paddingLeft: "15px",
                                fontWeight: 500,
                            }}>
                                Customer Name
                            </span>
                        }
                        name="customer_name"
                        required={false}
                    >
                        <Select
                            size="large"
                            // disabled={roleId === 2}
                            showSearch
                            optionFilterProp="children"
                            loading={isLoading}
                            placeholder="Select Customer Name"
                            className="CustomSelect"
                            onChange={(customerId) => {
                                const relatedProject = projects.find(
                                    (p: any) => p.clientId?._id === customerId
                                );

                                if (relatedProject) {
                                    form.setFieldsValue({
                                        project: relatedProject._id,
                                    });
                                } else {
                                    form.resetFields(["project"]);
                                }
                            }}
                        >
                            {customers.map((customer: any) => (
                                <Select.Option key={customer._id} value={customer._id}>
                                    {customer.name}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label={
                            <span style={{
                                color: '#4b5563',
                                fontFamily: 'inter, sans-serif',
                                fontSize: '14px',
                                paddingLeft: "15px",
                                fontWeight: 500,
                            }}>
                                Invoice Number
                            </span>
                        }
                        name="invoice_number"
                    >
                        <Row gutter={8}>
                            <Col span={4}>
                                <Input disabled value={prefix} className="Allinput text-[14px]" />
                            </Col>
                            <Col span={4}>
                                <Input disabled value={year} className="Allinput text-[14px]" />
                            </Col>
                            <Col span={12}>
                                <Input
                                    disabled
                                    value={formatSequence(invoiceSeq)}
                                    className="Allinput"
                                />
                            </Col>
                        </Row>
                    </Form.Item>

                    <Form.Item
                        label={
                            <span style={{
                                color: '#4b5563',
                                fontFamily: 'inter, sans-serif',
                                fontSize: '14px',
                                paddingLeft: "15px",
                                fontWeight: 500,
                            }}>
                                Invoice Date
                            </span>
                        }
                        name="invoice_date"
                        required={false}
                    >
                        <DatePicker className="Allinput" format="DD-MM-YYYY" style={{ width: '100%' }} />
                    </Form.Item>

                    <Form.Item
                        label={
                            <span style={{
                                color: '#4b5563',
                                fontFamily: 'inter, sans-serif',
                                fontSize: '14px',
                                paddingLeft: "15px",
                                fontWeight: 500,
                            }}>
                                Due Date
                            </span>
                        }
                        name="due_date"
                        required={false}
                    >
                        <DatePicker className="Allinput" format="DD-MM-YYYY" style={{ width: '100%' }} />
                    </Form.Item>

                    <Form.Item
                        name="currency"
                        label={
                            <span style={{
                                color: '#4b5563',
                                fontFamily: 'inter, sans-serif',
                                fontSize: '14px',
                                paddingLeft: "15px",
                                fontWeight: 500,
                            }}>
                                Currency
                            </span>
                        }
                        required={false}
                    >
                        <Select
                            placeholder="Select Currency"
                            className="CustomSelect"
                            showSearch
                            optionFilterProp="children"
                            // value={position}
                            onChange={(value) => {
                                // setPosition(value);
                                form.setFieldsValue({ technology: undefined });
                            }}
                        >
                            <Select.Option value="₹ INR">₹ INR</Select.Option>
                            <Select.Option value="$ USD">$ USD</Select.Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label={
                            <span style={{
                                color: '#4b5563',
                                fontFamily: 'inter, sans-serif',
                                fontSize: '14px',
                                paddingLeft: "15px",
                                fontWeight: 500,
                            }}>
                                Amount
                            </span>
                        }
                        name="amount"
                        required={false}
                    >
                        <Input type="Number" placeholder="Amount" className="Allinput" />
                    </Form.Item>

                    <Form.Item
                        label={
                            <span style={{
                                color: '#4b5563',
                                fontFamily: 'inter, sans-serif',
                                fontSize: '14px',
                                paddingLeft: "15px",
                                fontWeight: 500,
                            }}>
                                Description <span className="text-red-500">*</span>
                            </span>
                        }
                        name="description"
                        required={false}
                        rules={[{ required: true, message: "Please enter description" }]}
                    >
                        <Input.TextArea
                            placeholder="Enter description"
                            className="Allinputtextarea"
                        />
                    </Form.Item>
                </div>
                <Row justify="end">
                    <Col
                        xs={24}
                        sm={24}
                        md={24}
                        xl={24}
                        xxl={20}
                        className="d-flex justify-content-end me-4"
                    >
                        <div className="button-wrapper">
                            <button
                                className="allcanclebutton"
                                onClick={props.onClose}
                                type="button"
                                disabled={isLoading}
                            >
                                Cancel
                            </button>
                            <button
                                className="allsubmitebutton w-20"
                                type="submit"
                                disabled={isLoading}
                            >
                                {isLoading ? 'Adding...' : 'Add'}
                            </button>
                        </div>
                    </Col>
                </Row>
            </Form>
        </Modal>
    );
};

export default CopyInvoiceModel;